import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { getSignedMediaUrl } from '@/lib/utils';

import { MobileHamburgerMenu } from './MobileHamburgerMenu';
import type { EventBookingCategoryData } from '../../types/ticketBookingTypes';

import { PerkCarousel } from '../PerkCarousel';
interface MobileBookingCategoryDetailsProps {
  eventBookingCategory: EventBookingCategoryData;
  isAuthenticated?: boolean;
}

export const MobileBookingCategoryDetails: React.FC<MobileBookingCategoryDetailsProps> = ({ 
  eventBookingCategory, 
  isAuthenticated 
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const { booking_category } = eventBookingCategory;
  const { name, description, card_image_id, perks, media, business } = booking_category;

  // Track scrolling relative to the parent container setup
  const { scrollY } = useScroll({ container: scrollRef });

  // Create sliding app bar transition (opacity mapped 0 to 1 over scroll pixels 50->150)
  const appBarBgOpacity = useTransform(scrollY, [50, 150], [0, 1]);
  const appBarTextOpacity = useTransform(scrollY, [130, 180], [0, 1]);
  
  // Create fade out for the center text (opacity mapped 1 to 0 over scroll pixels 50->150)
  const heroTextOpacity = useTransform(scrollY, [50, 150], [1, 0]);
  const heroTextY = useTransform(scrollY, [0, 150], [0, -30]);

  // Parallax the background image specifically
  const bgY = useTransform(scrollY, [0, 400], [0, 120]);

  const handleBack = () => {
    navigate(-1);
  };

  const getMediaUrl = () => {
    if (card_image_id && card_image_id !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = card_image_id.includes('video') || card_image_id.includes('mp4') || card_image_id.includes('webm');
      return {
        url: getSignedMediaUrl(card_image_id, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }
    return null;
  };

  const heroMedia = getMediaUrl();
  const sortedMedia = media ? [...media].sort((a, b) => a.rank - b.rank) : [];

  return (
    <div ref={scrollRef} className="bg-[#050505] fixed inset-0 text-white font-sans w-full max-w-[100vw] z-[100] flex flex-col overflow-y-auto overflow-x-hidden overscroll-none">
      
      {/* 1. Fixed App Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)]"
        style={{
          backgroundColor: useTransform(
            appBarBgOpacity, 
            opacity => `rgba(10, 10, 10, ${opacity * 0.95})`
          ),
          backdropFilter: useTransform(
            appBarBgOpacity,
            opacity => `blur(${opacity * 20}px)`
          ),
          borderBottom: useTransform(
            appBarBgOpacity,
            opacity => `1px solid rgba(255,255,255, ${opacity * 0.1})`
          )
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent pointer-events-none opacity-80" />
        <div className="px-4 h-16 flex items-center justify-between relative z-10">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Go back"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Squeezed Title (Hidden until scrolled) */}
          <motion.div 
            style={{ opacity: appBarTextOpacity }}
            className="flex-1 px-4 text-center overflow-hidden"
          >
            <h2 className="text-lg font-serif font-bold text-white truncate max-w-full tracking-tight">
              {name}
            </h2>
          </motion.div>

          {/* Empty Placeholder to balance the flex container so the text stays centered */}
          <div className="w-10"></div>
        </div>
      </motion.div>

      {/* Hamburger Menu Container */}
      <div className="fixed top-0 right-0 h-[calc(4rem+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] px-4 flex items-center justify-end z-[110] pointer-events-none">
        <div className="pointer-events-auto">
          <MobileHamburgerMenu clubHandle={business?.handle || null} isAuthenticated={isAuthenticated} />
        </div>
      </div>

      {/* 2. Scrollable Hero Region (16:9 ratio enforced via padding bottom) */}
      <div 
        className="relative w-full overflow-hidden shadow-2xl z-10 bg-gray-900"
        style={{ paddingBottom: '56.25%' }}
      >
        <motion.div 
          style={{ y: bgY }}
           className="absolute inset-x-0 -top-10 bottom-0 w-full h-[120%] flex items-center justify-center -z-10"
        >
          {heroMedia ? (
            heroMedia.isVideo ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={heroMedia.url} type="video/mp4" />
                <source src={heroMedia.url} type="video/webm" />
              </video>
            ) : (
              <img
                src={heroMedia.url}
                alt={name}
                className="w-full h-full object-cover"
              />
            )
          ) : (
             <div 
              className={`w-full h-full bg-cover bg-center`}
              style={{ 
                backgroundImage: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        </motion.div>

        <motion.div 
          style={{
             opacity: heroTextOpacity,
             y: heroTextY
          }}
          className="absolute inset-x-0 bottom-0 top-0 flex flex-col items-center justify-center px-6 text-center z-20"
        >
          <div className="space-y-1.5 flex flex-col items-center max-w-sm">
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-widest drop-shadow-md">
              Booking Category
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-white tracking-tight drop-shadow-lg leading-tight line-clamp-2 pb-1">
              {name}
            </h1>
            {description && (
              <p className="text-sm text-gray-300 drop-shadow line-clamp-2 mt-1 px-4 text-center">
                {description}
              </p>
            )}
          </div>
        </motion.div>
      </div>
      
      <main className="flex-1 pb-32 relative z-10 w-full pt-8 px-4 flex flex-col gap-10">
        
        {/* About Section */}
        {description && (
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out fill-mode-both">
            <h2 className="text-xl font-bold font-serif mb-3 text-white">About</h2>
            <p className="text-gray-300 leading-relaxed text-[15px]">
              {description}
            </p>
          </section>
        )}

        {/* Perks Section elegantly displayed */}
        {perks && perks.length > 0 && (
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100 ease-out fill-mode-both">
            <PerkCarousel perks={perks} />
          </section>
        )}

        {/* Media Carousel */}
        {sortedMedia.length > 0 && (
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200 ease-out fill-mode-both">
            <h2 className="text-xl font-bold font-serif mb-4 text-white">Gallery</h2>
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 scrollbar-hide">
              {sortedMedia.map((m, idx) => {
                const isVideo = (m.media_type || '').toLowerCase() === 'video';
                const mediaUrl = getSignedMediaUrl(m.media_id, isVideo ? 'video' : 'image').url;

                return (
                  <div 
                    key={idx} 
                    className="flex-none w-[80%] aspect-[4/3] snap-center rounded-2xl overflow-hidden bg-gray-900 border border-white/10 shadow-lg relative"
                  >
                    {isVideo ? (
                      <video
                        src={mediaUrl}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={`Gallery item ${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

    </div>
  );
};
